import React, { useState } from 'react';

interface AddEndpointModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (url: string) => void;
}

export default function AddEndpointModal({ isOpen, onClose, onConfirm }: AddEndpointModalProps) {
    const [url, setUrl] = useState('');
    const [error, setError] = useState('');

    if (!isOpen) return null;

    const validateUrl = (value: string) => {
        if (!value) {
            setError('URL is required');
            return false;
        }
        try {
            const parsedUrl = new URL(value);
            if (parsedUrl.protocol !== 'https:' && parsedUrl.protocol !== 'http:') {
                setError('URL must start with http:// or https://');
                return false;
            }
            // Basic check for hostname
            if (!parsedUrl.hostname || parsedUrl.hostname.indexOf('.') === -1) {
                // Allow localhost for testing, otherwise require a dot
                if (parsedUrl.hostname !== 'localhost') {
                    setError('Enter a valid domain');
                    return false;
                }
            }
            setError('');
            return true;
        } catch (e) {
            setError('Enter a valid URL');
            return false;
        }
    };

    const handleConfirm = () => {
        if (validateUrl(url)) {
            onConfirm(url);
            setUrl(''); // Reset on success
        }
    };

    const handleContainerClick = (e: React.MouseEvent) => {
        // Prevent closing when clicking inside the modal
        e.stopPropagation();
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity"
            onClick={onClose}
        >
            <div
                className="w-full max-w-sm rounded-3xl p-6 shadow-2xl flex flex-col gap-6"
                style={{ background: 'var(--modal-bg)', border: '1px solid var(--color-surface-border)' }}
                onClick={handleContainerClick}
            >
                {/* Header Subtitles */}
                <div className="flex flex-col gap-2">
                    <h2 className="text-xl font-bold text-txt-heading tracking-tight">Add new endpoint</h2>
                    <p className="text-[15px] text-txt-secondary leading-snug">
                        Enter the URL where you'll receive notifications.
                    </p>
                </div>

                {/* Input Area */}
                <div className="flex flex-col gap-2">
                    <label className="text-[13px] font-bold text-txt-heading tracking-wide">
                        Webhook URL
                    </label>
                    <div className="relative flex items-center">
                        <span className="material-symbols-outlined absolute left-3 text-txt-muted text-[20px] pointer-events-none">
                            link
                        </span>
                        <input
                            type="text"
                            value={url}
                            onChange={(e) => {
                                setUrl(e.target.value);
                                if (error) validateUrl(e.target.value);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleConfirm();
                            }}
                            placeholder="https://example.com/webhook"
                            className="w-full rounded-xl py-3 pl-10 pr-4 text-txt-heading text-[15px] placeholder:text-txt-muted focus:outline-none focus:border-electric-blue/50 focus:ring-1 focus:ring-electric-blue/50 transition-all font-medium"
                            style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)' }}
                        />
                    </div>
                    {error && (
                        <p className="text-red-400 text-xs font-medium mt-1">{error}</p>
                    )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 pt-2">
                    <button
                        onClick={onClose}
                        className="flex-1 py-3 rounded-xl text-[15px] font-bold text-txt-heading bg-transparent hover:bg-surface-hover transition-colors cursor-pointer"
                        style={{ border: '1px solid var(--color-surface-border)' }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        className="flex-1 py-3 rounded-xl text-[15px] font-bold text-white bg-electric-blue hover:bg-electric-blue/90 border border-transparent shadow-[0_0_15px_rgba(19,127,236,0.3)] transition-all"
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
}
